import type { DdxSetup } from 'store/planStore';
import type { DdxSetupRequestItem } from './types';

import { postData } from './utils';

export const DDX_SETUP_KEY = 'ddx-setup';

export interface ApiResponse<T> {
  status: string;
  data: T;
}

type DdxSetupRawCountry = {
  isMustHave?: string | number | null;
  isPercentage?: string | number | boolean | null; // now "0.02" usually
  tiers?: Array<
    Partial<
      Record<'fixedTier1' | 'fixedTier2' | 'fixedTier3', string | number | null>
    >
  > | null;
};

type DdxSetupRawByCountry = Record<
  string,
  DdxSetupRawCountry | null | undefined
>;

export type DdxSetupByCountry = Record<string, Partial<DdxSetup> | undefined>;

function toNumberOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeSetup(
  raw: DdxSetupRawCountry | null | undefined
): Partial<DdxSetup> | undefined {
  if (!raw) return undefined;

  const isMustHave = toNumberOrNull(raw.isMustHave);

  const percentageFloat = toNumberOrNull(raw.isPercentage);
  const isPercentage =
    percentageFloat == null ? null : Number(percentageFloat.toFixed(6));

  const tiersRaw = raw.tiers;
  const tiers =
    Array.isArray(tiersRaw) && tiersRaw.length
      ? tiersRaw
          .map((t) => {
            if (!t || typeof t !== 'object') return null;

            const key = Object.keys(t)[0] as
              | 'fixedTier1'
              | 'fixedTier2'
              | 'fixedTier3'
              | undefined;

            if (!key) return null;

            const val = toNumberOrNull((t as any)[key]);
            return { [key]: val } as any;
          })
          .filter(Boolean)
      : undefined;

  const isEmpty =
    isMustHave == null &&
    isPercentage == null &&
    (tiers == null || tiers.length === 0);

  if (isEmpty) return undefined;

  return {
    isMustHave,
    isPercentage,
    tiers,
  };
}

function normalizeByCountry(
  raw: DdxSetupRawByCountry | null | undefined
): DdxSetupByCountry {
  const out: DdxSetupByCountry = {};
  if (!raw) return out;

  Object.entries(raw).forEach(([countryISO, cfg]) => {
    out[countryISO] = normalizeSetup(cfg ?? undefined);
  });

  return out;
}

export const fetchDDXSetup = async (
  ddxSetupRequest: DdxSetupRequestItem[]
): Promise<ApiResponse<DdxSetupByCountry>> => {
  const res = await postData<ApiResponse<DdxSetupRawByCountry>>(
    `/campaign/v3/ddx-setup`,
    ddxSetupRequest
  );

  return {
    ...res,
    data: normalizeByCountry(res.data),
  };
};
